import { useCallback, useEffect, useRef, useState } from 'react';
import jsQRLib from 'jsqr';
import { Camera, X, ScanLine } from 'lucide-react';
import Button from './Button.js';
import { parseShipmentIdFromQrData } from '../../utils/parseQrShipmentId.js';
import { ensureCameraPermission } from '../../utils/devicePermissions.js';

const jsQR = typeof jsQRLib === 'function' ? jsQRLib : jsQRLib?.default;

const SCAN_COOLDOWN_MS = 1800;
const SCAN_INTERVAL_MS = 180;
const JSQR_MAX_WIDTH = 720;

function supportsLiveQrScan() {
  return Boolean(
    typeof navigator !== 'undefined'
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === 'function',
  );
}

const getCameraStream = async () => {
  const attempts = [
    {
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    },
    { video: { facingMode: 'environment' }, audio: false },
    { video: true, audio: false },
  ];
  let lastError;
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('Camera unavailable');
};

const canvasToJpegFile = (canvas) =>
  new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve(new File([blob], `qr-capture-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.85,
    );
  });

const createBarcodeDetector = async () => {
  if (typeof window === 'undefined' || !('BarcodeDetector' in window)) return null;
  try {
    if (typeof window.BarcodeDetector.getSupportedFormats === 'function') {
      const formats = await window.BarcodeDetector.getSupportedFormats();
      if (Array.isArray(formats) && formats.length && !formats.includes('qr_code')) {
        return null;
      }
    }
    return new window.BarcodeDetector({ formats: ['qr_code'] });
  } catch {
    return null;
  }
};

const detectWithBarcodeDetector = async (detector, video, canvas) => {
  if (!detector) return '';
  try {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(video);
      try {
        const codes = await detector.detect(bitmap);
        const value = codes?.[0]?.rawValue;
        if (value) return String(value);
      } finally {
        bitmap.close();
      }
    }
  } catch {
    // fall through to canvas detect
  }
  try {
    const codes = await detector.detect(canvas);
    const value = codes?.[0]?.rawValue;
    return value ? String(value) : '';
  } catch {
    return '';
  }
};

const detectWithJsQr = (canvas) => {
  if (typeof jsQR !== 'function') return '';
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return '';
  const { width, height } = canvas;
  if (!width || !height) return '';
  const imageData = ctx.getImageData(0, 0, width, height);
  const code = jsQR(imageData.data, width, height, { inversionAttempts: 'attemptBoth' });
  return code?.data ? String(code.data) : '';
};

/**
 * In-app live QR scanner — auto-detects QR from camera, captures a still,
 * and passes the decoded payload upstream for processing.
 */
const LiveQrScanner = ({
  open,
  onClose,
  onScan,
  title = 'Scan QR Code',
  hint = 'Point the camera at the QR. It will detect automatically.',
  continuous = true,
  busy = false,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const detectorRef = useRef(null);
  const lastScanRef = useRef({ value: '', at: 0 });
  const scanningRef = useRef(false);
  const runningRef = useRef(false);
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);
  const continuousRef = useRef(continuous);
  const busyRef = useRef(busy);
  const [starting, setStarting] = useState(false);
  const [active, setActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  onScanRef.current = onScan;
  onCloseRef.current = onClose;
  continuousRef.current = continuous;
  busyRef.current = busy;

  const stopLoop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopCamera = useCallback(() => {
    runningRef.current = false;
    scanningRef.current = false;
    stopLoop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setActive(false);
    setProcessing(false);
  }, [stopLoop]);

  const drawVideoFrame = useCallback((video) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;
    const scale = Math.min(1, JSQR_MAX_WIDTH / width);
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas;
  }, []);

  const handleDetection = useCallback(async (rawValue, canvas) => {
    const text = String(rawValue || '').trim();
    if (!text) return;

    const now = Date.now();
    if (
      text === lastScanRef.current.value
      && now - lastScanRef.current.at < SCAN_COOLDOWN_MS
    ) {
      return;
    }
    lastScanRef.current = { value: text, at: now };

    const shipmentId = parseShipmentIdFromQrData(text);
    const imageFile = canvas ? await canvasToJpegFile(canvas) : null;
    const shouldClose = !continuousRef.current;
    setProcessing(true);
    runningRef.current = false;
    stopLoop();
    const scanResult = onScanRef.current?.({
      qrData: text,
      shipmentId: shipmentId || '',
      imageFile,
    });
    if (shouldClose) {
      stopCamera();
      onCloseRef.current?.();
    }
    try {
      await Promise.resolve(scanResult);
    } finally {
      if (!shouldClose) {
        setProcessing(false);
      }
    }
  }, [stopCamera, stopLoop]);

  const scanOnce = useCallback(async () => {
    const video = videoRef.current;
    if (
      !runningRef.current
      || scanningRef.current
      || busyRef.current
      || !video
      || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
    ) {
      return;
    }

    scanningRef.current = true;
    try {
      const canvas = drawVideoFrame(video);
      if (!canvas) return;
      const fromDetector = await detectWithBarcodeDetector(detectorRef.current, video, canvas);
      const decoded = fromDetector || detectWithJsQr(canvas);
      if (decoded) {
        await handleDetection(decoded, canvas);
      }
    } catch {
      // ignore transient detect errors
    } finally {
      scanningRef.current = false;
    }
  }, [drawVideoFrame, handleDetection]);

  const scheduleScan = useCallback(() => {
    stopLoop();
    if (!runningRef.current) return;
    timerRef.current = setTimeout(async () => {
      await scanOnce();
      if (runningRef.current) scheduleScan();
    }, SCAN_INTERVAL_MS);
  }, [scanOnce, stopLoop]);

  const startCamera = useCallback(async () => {
    if (!supportsLiveQrScan()) {
      setError('Live QR scan is not supported on this device. Use upload instead.');
      return;
    }

    setError('');
    setStarting(true);
    stopCamera();

    try {
      const allowed = await ensureCameraPermission();
      if (!allowed) {
        throw Object.assign(new Error('Camera permission denied'), { name: 'NotAllowedError' });
      }
      detectorRef.current = await createBarcodeDetector();
      const stream = await getCameraStream();
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error('Camera preview unavailable');

      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('autoplay', 'true');
      video.muted = true;
      video.srcObject = stream;
      await video.play();
      runningRef.current = true;
      setActive(true);
      scheduleScan();
    } catch (err) {
      console.error('Live QR camera failed:', err);
      setError(
        err?.name === 'NotAllowedError'
          ? 'Camera permission denied. Allow camera access in app settings.'
          : 'Could not open camera. Try upload instead.',
      );
      stopCamera();
    } finally {
      setStarting(false);
    }
  }, [scheduleScan, stopCamera]);

  const startCameraRef = useRef(startCamera);
  startCameraRef.current = startCamera;

  useEffect(() => {
    if (!open) {
      stopCamera();
      setError('');
      return undefined;
    }
    startCameraRef.current();
    return () => stopCamera();
  }, [open, stopCamera]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-lg bg-gray-900 rounded-xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-950 text-white">
          <div className="flex items-center gap-2 min-w-0">
            <ScanLine className="w-5 h-5 text-orange-400 shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold truncate">{title}</h3>
              <p className="text-xs text-gray-400 truncate">{hint}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10"
            aria-label="Close scanner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative aspect-[4/3] bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />
          <canvas ref={canvasRef} className="hidden" />
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-[min(72%,240px)] aspect-square border-2 border-orange-400/90 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>
          {(starting || !active || processing || busy) && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-sm">
              <Camera className="w-5 h-5 mr-2 animate-pulse" />
              {processing || busy ? 'Processing QR…' : 'Starting camera...'}
            </div>
          )}
        </div>

        <div className="p-4 space-y-3 bg-gray-950">
          {error ? (
            <p className="text-sm text-red-300">{error}</p>
          ) : (
            <p className="text-sm text-emerald-300">
              {processing || busy
                ? 'QR captured. Sending for processing…'
                : active
                  ? 'Auto-detecting QR… hold steady'
                  : 'Preparing scanner…'}
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" fullWidth onClick={onClose}>
              Close
            </Button>
            {!active && (
              <Button variant="primary" icon={Camera} fullWidth onClick={startCamera} loading={starting}>
                Retry Camera
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveQrScanner;
export { supportsLiveQrScan };
